import { useEffect, useState } from "react";
import { Plus, Building2, Mail, Phone, Globe, Pencil, Trash2 } from "lucide-react";
import api from "../lib/api";
import { BACKEND } from "../lib/backend";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";

const empty = {
  name: "", caption: "", file_name: "", logo_url: "",
  email: "", phone: "", website: "", notes: "",
};

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
    } catch {
      toast.error("Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, caption: c.caption || "", file_name: c.file_name || "",
      logo_url: c.logo_url || "", email: c.email || "", phone: c.phone || "",
      website: c.website || "", notes: c.notes || "",
    });
    setOpen(true);
  };

  const onLogoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/uploads/image", fd);
      setForm((s) => ({ ...s, logo_url: data.url, file_name: data.original_name }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/companies/${editing.id}`, form);
        toast.success("Company updated");
      } else {
        await api.post("/companies", form);
        toast.success("Company added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <AppLayout>
      <div className="p-8 md:p-12 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="label-eyebrow mb-2">Database</div>
            <h1 className="font-display text-4xl font-bold">Companies</h1>
            <p className="text-zinc-400 mt-2 text-sm">Reusable advertiser records — logo, contact, notes.</p>
          </div>
          <Button data-testid="add-company-btn" onClick={openNew} className="bg-blue-600 hover:bg-blue-500">
            <Plus className="w-4 h-4 mr-2" /> Add Company
          </Button>
        </div>

        {loading ? (
          <div className="text-zinc-500">Loading…</div>
        ) : companies.length === 0 ? (
          <div className="border border-dashed border-zinc-800 rounded-md p-16 text-center">
            <Building2 className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
            <h3 className="font-display text-xl font-semibold mb-2">No companies yet</h3>
            <p className="text-sm text-zinc-400 mb-6">Add companies manually or import them when building a journal.</p>
            <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-500" data-testid="empty-add-company">
              <Plus className="w-4 h-4 mr-2" /> Add Company
            </Button>
          </div>
        ) : (
          <div data-testid="companies-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {companies.map((c) => (
              <div key={c.id} data-testid={`company-${c.id}`} className="bg-zinc-900 border border-zinc-800 rounded-md p-5 group">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-md bg-zinc-950 border border-zinc-800 grid place-items-center overflow-hidden shrink-0">
                    {c.logo_url ? (
                      <img src={`${BACKEND}${c.logo_url}`} alt={c.name} className="w-full h-full object-contain" />
                    ) : (
                      <Building2 className="w-5 h-5 text-zinc-600" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display font-semibold truncate">{c.name}</h3>
                    {c.caption && <p className="text-sm text-zinc-400 line-clamp-2 mt-1">{c.caption}</p>}
                    <div className="text-xs text-zinc-500 mt-2 space-y-1">
                      {c.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</div>}
                      {c.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</div>}
                      {c.website && <div className="flex items-center gap-1 truncate"><Globe className="w-3 h-3" /> {c.website}</div>}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button data-testid={`edit-company-${c.id}`} onClick={() => openEdit(c)} className="text-xs text-zinc-400 hover:text-zinc-50 px-2 py-1 rounded hover:bg-zinc-800 flex items-center gap-1">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button data-testid={`delete-company-${c.id}`} onClick={() => remove(c)} className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-red-950/40 flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-display">{editing ? "Edit company" : "New company"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2 col-span-2">
                  <Label className="label-eyebrow">Name</Label>
                  <Input data-testid="company-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-zinc-950 border-zinc-800" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="label-eyebrow">Caption / Tagline</Label>
                  <Textarea data-testid="company-form-caption" rows={2} value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="bg-zinc-950 border-zinc-800" />
                </div>
                <div className="space-y-2">
                  <Label className="label-eyebrow">Email</Label>
                  <Input data-testid="company-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-950 border-zinc-800" />
                </div>
                <div className="space-y-2">
                  <Label className="label-eyebrow">Phone</Label>
                  <Input data-testid="company-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-zinc-950 border-zinc-800" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="label-eyebrow">Website</Label>
                  <Input data-testid="company-form-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="bg-zinc-950 border-zinc-800" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="label-eyebrow">Logo</Label>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-md bg-zinc-950 border border-zinc-800 grid place-items-center overflow-hidden">
                      {form.logo_url ? (
                        <img src={`${BACKEND}${form.logo_url}`} alt="" className="w-full h-full object-contain" />
                      ) : <Building2 className="w-5 h-5 text-zinc-600" />}
                    </div>
                    <Input data-testid="company-form-logo" type="file" accept="image/*,.pdf,.svg,.eps,.ai,.tiff,.tif,.heic,.heif,.ico,application/pdf,image/svg+xml" onChange={onLogoChange} className="bg-zinc-950 border-zinc-800" />
                  </div>
                  {uploading && <div className="text-xs text-zinc-500">Uploading…</div>}
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="label-eyebrow">Notes</Label>
                  <Textarea data-testid="company-form-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-zinc-950 border-zinc-800" />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="company-form-submit" className="bg-blue-600 hover:bg-blue-500">
                  {editing ? "Save changes" : "Create company"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
